import { ArcherContainer, ArcherElement } from 'react-archer'
import { Box, Button, Typography, IconButton } from '@mui/material'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useAppTheme } from '@/theme/ThemeContext'
import { useEffect, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import CropFreeIcon from '@mui/icons-material/CropFree'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {
  getWaterTreeHierarchyMap,
  WaterTreeHierarchyNode,
} from '@/services/dashboardService'
import axios from 'axios'
import { debugLog } from '@/utils/debugLog'

// ── Single node card ──────────────────────────────────────────────────────────
function TreeNode({
  id,
  name,
  isCenter = false,
  isAlternative = false,
  relations = [],
  onClick,
  isDarkMode,
  scale = 1.0,
}: {
  id: string
  name: string
  isCenter?: boolean
  isAlternative?: boolean
  relations?: any[]
  onClick?: () => void
  isDarkMode: boolean
  scale?: number
}) {
  const cardBg = isDarkMode 
    ? (isCenter ? '#1D325A' : '#202B40') 
    : '#FFFFFF'
  const borderColor = isDarkMode
    ? (isAlternative ? '#94A3B8' : '#43597F')
    : (isCenter ? '#72A4EB' : 'rgba(50, 59, 119, 0.1)') // #323B771A
  const textColor = isDarkMode
    ? '#FFFFFF'
    : '#003F61'
  const subTextColor = isDarkMode
    ? (isCenter ? 'rgba(255, 255, 255, 0.8)' : 'rgba(187, 215, 255, 0.9)')
    : (isCenter ? '#72A4EB' : '#94A3B8')

  const width = 240 * scale
  const py = 18 * scale
  const px = 28 * scale
  const fontSizeMain = 15 * scale
  const fontSizeSub = 10 * scale

  return (
    <ArcherElement id={id} relations={relations}>
      <Box
        onClick={onClick}
        sx={{
          width,
          py: `${py}px`,
          px: `${px}px`,
          borderRadius: `${8 * scale}px`,
          border: isCenter
            ? `${2.5 * scale}px solid #72A4EB`
            : `${1.5 * scale}px ${isAlternative ? 'dashed' : 'solid'} ${borderColor}`,
          background: cardBg,
          textAlign: 'center',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.15s ease-out',
          boxShadow: isCenter
            ? `0 0 ${40 * scale}px rgba(114, 164, 235, 0.2)`
            : '0 4px 16px rgba(0,0,0,0.25)',
          '&:hover': onClick
            ? {
                borderColor: '#72A4EB',
                transform: 'translateY(-5px)',
                boxShadow: isDarkMode ? '0 12px 32px rgba(0,0,0,0.45)' : '0 12px 24px rgba(0,0,0,0.1)',
                bgcolor: isDarkMode ? '#2A3752' : '#F8FAFC',
              }
            : {},
        }}
      >
        <Typography sx={{
          color: subTextColor,
          fontFamily: 'Rubik, sans-serif',
          fontSize: `${fontSizeSub}px`,
          fontWeight: 700,
          mb: 0.6 * scale,
          textTransform: 'uppercase',
          letterSpacing: `${1 * scale}px`,
        }}>
          {isAlternative ? 'הורה חלופי' : 'עץ מים'}
        </Typography>
        <Typography sx={{
          color: textColor,
          fontFamily: 'Rubik, sans-serif',
          fontSize: `${fontSizeMain}px`,
          fontWeight: 900,
          lineHeight: 1.2,
          letterSpacing: `${0.4 * scale}px`,
        }}>
          {name}
        </Typography>
      </Box>
    </ArcherElement>
  )
}

const ZoomControls = ({
    onZoomIn,
    onZoomOut,
    onReset,
    isLocked,
    onToggleLock,
    isDarkMode,
}: {
    onZoomIn: () => void
    onZoomOut: () => void
    onReset: () => void
    isLocked: boolean
    onToggleLock: () => void
    isDarkMode: boolean
}) => (
    <Box sx={{
        position: 'absolute',
        bottom: 30,
        left: 30,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        bgcolor: isDarkMode ? '#192438' : '#FFFFFF',
        borderRadius: '8px',
        p: '4px',
        border: isDarkMode ? '1.2px solid #43597F' : '1.2px solid #EAEDF2',
        boxShadow: isDarkMode ? '0 12px 48px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.1)',
        zIndex: 100,
        width: '40px',
        alignItems: 'center',
    }}>
        <IconButton onClick={onZoomIn} size="small" sx={{ color: isDarkMode ? '#BBD7FF' : '#003F61', p: 0.8, '&:hover': { bgcolor: isDarkMode ? 'rgba(187, 215, 255, 0.15)' : '#F1F5F9' } }}>
            <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton onClick={onZoomOut} size="small" sx={{ color: isDarkMode ? '#BBD7FF' : '#003F61', p: 0.8, '&:hover': { bgcolor: isDarkMode ? 'rgba(187, 215, 255, 0.15)' : '#F1F5F9' } }}>
            <RemoveIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton onClick={onReset} size="small" sx={{ color: isDarkMode ? '#BBD7FF' : '#003F61', p: 0.8, '&:hover': { bgcolor: isDarkMode ? 'rgba(187, 215, 255, 0.15)' : '#F1F5F9' } }}>
            <CropFreeIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton onClick={onToggleLock} size="small" sx={{ color: isLocked ? '#72A4EB' : (isDarkMode ? '#BBD7FF' : '#003F61'), p: 0.8, '&:hover': { bgcolor: isDarkMode ? 'rgba(187, 215, 255, 0.15)' : '#F1F5F9' } }}>
            <LockOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
    </Box>
)

export default function WaterTreeMap() {
  const { treeName } = useOutletContext<{
    treeName: string
    zoneType?: string
  }>()
  const { siteId, consumerId } = useParams()
  const navigate = useNavigate()
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [scale, setScale] = useState(1.0)
  const [isLocked, setIsLocked] = useState(false)
  const [nodes, setNodes] = useState<WaterTreeHierarchyNode[]>([])
  const [isPhysicalTree, setIsPhysicalTree] = useState(true)
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'failed'>('loading')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!consumerId) return
    const accountId = localStorage.getItem('accountId')
    if (!accountId) return

    setIsPhysicalTree(true)
    setLoadState('loading')
    debugLog('[WaterTreeMap] Fetching map for:', { accountId, consumerId })
    
    getWaterTreeHierarchyMap(accountId, consumerId)
      .then(data => {
        debugLog('[WaterTreeMap] Hierarchy Data SUCCESS:', data)
        setNodes(data)
        setLoadState('loaded')
      })
      .catch(err => {
        debugLog('[WaterTreeMap] Hierarchy Data ERROR:', err.response?.status, err.response?.data)
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setIsPhysicalTree(false)
          setNodes([])
          setLoadState('loaded')
        } else {
          console.error('[WaterTreeMap] hierarchy-map fetch error:', err)
          setNodes([])
          setLoadState('failed')
        }
      })
  }, [consumerId, attempt])

  const { parents, children, alternativeParents } = useMemo(() => ({
    parents: nodes.filter(n => n.relationship === 'parent'),
    children: nodes.filter(n => n.relationship === 'child'),
    alternativeParents: nodes.filter(n => n.relationship === 'alternative_parent'),
  }), [nodes])

  const handleZoomIn = () => !isLocked && setScale(prev => Math.min(prev + 0.1, 1.8))
  const handleZoomOut = () => !isLocked && setScale(prev => Math.max(prev - 0.1, 0.6))
  const handleReset = () => setScale(1.0)
  const toggleLock = () => setIsLocked(prev => !prev)

  const lineColor = isDarkMode ? 'rgba(187, 215, 255, 0.35)' : 'rgba(0, 98, 255, 0.25)'
  const scaledStrokeWidth = 2.5 * scale

  const goTo = (premiseId: number) =>
    navigate(`/${siteId}/analytics/tree/${premiseId}/details`)

  const topRow = [...parents, ...alternativeParents]

  return (
    <Box sx={{
        width: '100%',
        direction: 'rtl',
        position: 'relative',
        minHeight: '750px',
        overflow: 'auto',
        p: 2,
    }}>

      <ZoomControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
        isLocked={isLocked}
        onToggleLock={toggleLock}
        isDarkMode={isDarkMode}
      />

      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        pt: `${30 * scale}px`,
      }}>
        <ArcherContainer
            key={`archer-sync-scale-v2-${scale}-${nodes.length}`}
            strokeColor={lineColor}
            strokeWidth={scaledStrokeWidth}
            style={{ width: '100%' }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: `${70 * scale}px`,
                py: `${30 * scale}px`,
            }}>

                {topRow.length > 0 && (
                  <Box sx={{ display: 'flex', gap: `${50 * scale}px`, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {topRow.map((p) => (
                      <TreeNode
                          key={`p-${p.premiseId}`}
                          id={`p-${p.premiseId}`}
                          name={p.nodeName}
                          isAlternative={p.relationship === 'alternative_parent'}
                          isDarkMode={isDarkMode}
                          scale={scale}
                          relations={[{
                              targetId: 'center',
                              sourceAnchor: 'bottom' as const,
                              targetAnchor: 'top' as const,
                              style: {
                                  strokeColor: lineColor,
                                  strokeWidth: scaledStrokeWidth,
                                  noCurves: true,
                                  endMarker: false,
                                  strokeDasharray: p.relationship === 'alternative_parent' ? '6,4' : undefined,
                              },
                          }]}
                        onClick={() => goTo(p.premiseId)}
                      />
                      ))}
                  </Box>
                )}

                <TreeNode
                    id="center"
                    name={treeName || 'עץ מים'}
                    isCenter
                    isDarkMode={isDarkMode}
                    scale={scale}
                />

                {children.length > 0 && (
                  <Box sx={{ display: 'flex', gap: `${50 * scale}px`, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {children.map((c) => (
                      <TreeNode
                          key={`c-${c.premiseId}`}
                          id={`c-${c.premiseId}`}
                          name={c.nodeName}
                          isDarkMode={isDarkMode}
                          scale={scale}
                          relations={[{
                              targetId: 'center',
                              sourceAnchor: 'top' as const,
                              targetAnchor: 'bottom' as const,
                              style: { strokeColor: lineColor, strokeWidth: scaledStrokeWidth, noCurves: true, endMarker: false },
                          }]}
                        onClick={() => goTo(c.premiseId)}
                      />
                      ))}
                  </Box>
                )}

                {loadState === 'failed' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 4 }}>
                    <Typography sx={{ color: isDarkMode ? '#FCA5A5' : '#B91C1C', fontFamily: 'Rubik, sans-serif', textAlign: 'center' }}>
                      טעינת מפת העץ נכשלה
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => setAttempt(n => n + 1)}>
                      נסה שוב
                    </Button>
                  </Box>
                )}

                {loadState === 'loaded' && nodes.length === 0 && (
                  <Typography sx={{ color: '#94A3B8', fontFamily: 'Rubik, sans-serif', mt: 4, textAlign: 'center' }}>
                    {isPhysicalTree
                      ? 'אין מבנה היררכיה זמין לעץ זה'
                      : 'מפת עצי מים זמינה רק לעצים פיזיים, לא לקבוצות'}
                  </Typography>
                )}
            </Box>
        </ArcherContainer>
      </Box>
    </Box>
  )
}
